import restaurantDetailsQuery from './restaurantDetailsQuery.graphql';
import checkErrors from '../checkErrors';
import { permanentRedirect, success } from '../../util/Response';
import { createError } from '../createError';
import createVariables from './createVariables';

// The query wraps the entity, so a miss comes back as `{ restaurant: null }`.
// Testing the wrapper instead of the entity made every unknown restaurant a
// success -- a 200 whose render then blew up in MetaData on a null name.
const hasResults = data => data != null && data.restaurant != null;

// A duplicate listing folded into another restaurant: the API answers with the
// kept restaurant and its path, and the old URL answers 301 there so search
// engines move the page's ranking across instead of finding a dead page.
const redirectMergedDuplicate = response =>
    response.flatMap(data =>
        data.restaurant.movedTo
            ? permanentRedirect(data.restaurant.movedTo)
            : success(data),
    );

const fetchData = ({ client, location }) => {
    return resultsFetchData({ resultsQuery: restaurantDetailsQuery })({
        client,
        location,
    });
};

const slugRegex = /^(?:.*[-/])?([a-zA-Z0-9-_]+)$/;

const extractId = pathname => {
    const matched = slugRegex.exec(pathname);
    return matched ? matched[1] : null;
};

const resultsFetchData =
    ({ resultsQuery }) =>
    ({ client, location, fetchPolicy = 'cache-first' }) => {
        const { pathname } = location;
        const restaurantId = extractId(pathname);

        const searchParams = {
            restaurantId,
        };

        return client
            .query({
                query: resultsQuery,
                variables: createVariables(searchParams),
                fetchPolicy,
            })
            .then(checkErrors(hasResults, 'restaurants'))
            .then(redirectMergedDuplicate)
            .catch(createError({ dataBeingFetched: 'restaurants' }));
    };

// The same fetch (status codes, redirects, error mapping) with another query
// document: the new detail page (W3.1) asks for a few extra fields.
export const fetchDataWithQuery =
    resultsQuery =>
    ({ client, location }) =>
        resultsFetchData({ resultsQuery })({ client, location });

export default fetchData;
